import React from 'react';

import { Skeleton, Tag } from 'antd';
import { Avatar, AvatarShape, Column, Row, Space, Text } from 'farmhub-ui-core';

import { useAppDispatch, useAppSelector } from 'app';
import { dataEmpty, Empty, InfoCard, ShortFilterTable } from 'components';
import { findProductsToConnect, selectProductUnit } from 'slice';
import { theme } from 'styled';
import {
  formatImageUrl,
  formatMoney,
  genWholesaleUnit,
  getDummyArray,
  getRelativeQuantityColor,
  IMAGETYPE,
  renderTableSkeleton,
  reportQuantity,
  SIZE,
  paginationInit,
} from 'utils';

import { CTable, CTag } from './styles';

const tableSkeleton = [
  {
    title: <Skeleton.Input size="small" style={{ width: 100 }} />,
    type: 'image',
    width: 100,
  },
  {
    title: <Skeleton.Input size="small" style={{ width: 100 }} />,
    type: 'input',
    width: 200,
    align: 'right',
  },
];

interface ProductStepDesktopProps {
  myType?: string;
  supplierID?: string;
  dataFilter?: any;
  handleFilterFormChange?: (e: any) => void;
  selectedProducts: string[];
  setSelectedProducts: (value: string[]) => void;
  selectAllProducts: boolean;
  setSelectAllProducts: (value: boolean) => void;
  setTotalProduct: (value: number) => void;
  totalProduct: number;
  filterForm?: any;
  query?: any;
  setQuery: (e: any) => void;
}

const ProductStepDesktop = ({
  myType,
  supplierID,
  dataFilter,
  handleFilterFormChange,
  selectedProducts,
  setSelectedProducts,
  selectAllProducts,
  setSelectAllProducts,
  setTotalProduct,
  totalProduct,
  filterForm,
  query,
  setQuery,
}: ProductStepDesktopProps): JSX.Element => {
  const dispatch = useAppDispatch();
  const [isLoading, setIsLoading] = React.useState(true);
  const [products, setProducts] = React.useState([]);
  const productUnit = useAppSelector(selectProductUnit);
  const [pagination, setPagination] = React.useState(paginationInit);

  const handleSelectProduct = (selectedRowKeys: any[]): void => {
    setSelectedProducts(selectedRowKeys as string[]);
  };

  const handleSelectAllProductsTags = (checked: any): void => {
    setSelectAllProducts(checked);
    if (!checked) {
      setSelectedProducts([]);
    }
  };

  const getSelectAllTitle = (): string => {
    if (selectAllProducts) {
      return `Bỏ chọn tất cả ${totalProduct} sản phẩm`;
    }
    return `Chọn tất cả ${totalProduct} sản phẩm`;
  };

  const handleChangePagination = (e): void => {
    setQuery({
      ...query,
      page: e.current,
      take: e.pageSize,
    });
  };

  React.useEffect(() => {
    const getInvitationProducts = async (): Promise<void> => {
      setIsLoading(true);

      const options = {
        order: { createdAt: 0 },
        entityID: supplierID,
        status: 0,
        ...query,
      };

      try {
        const result: any = await dispatch(
          findProductsToConnect(options)
        ).unwrap();

        const { data, pagination: paginationResult } = result;
        setPagination(paginationResult);
        setTotalProduct(paginationResult.total);
        const formattedList = data.map((item: any) => {
          return {
            ...item.product,
            key: item.product.id,
          };
        });
        setProducts(formattedList);
      } catch (error) {
        console.log(
          'An error occurred while getting invitation products',
          error
        );
      } finally {
        setIsLoading(false);
      }
    };

    getInvitationProducts();
  }, [dispatch, query, myType, supplierID, setTotalProduct]);

  const columns = [
    {
      width: 240,
      title: (
        <Text color="primary" weight="bold">
          Sản phẩm{' '}
          {!selectAllProducts && <>(đã chọn {selectedProducts.length})</>}
        </Text>
      ),
      dataIndex: 'id',
      key: 'id',
      render: (id, record) => {
        const { name, avatar } = record;
        return (
          <InfoCard
            img={
              <Avatar
                shape={AvatarShape.Square}
                size={theme.avatar.size.sm}
                src={formatImageUrl(avatar, IMAGETYPE.AVATAR.xxs)}
              />
            }
            info={
              <Row
                styles={{
                  fontSize: theme.font.size.xxs,
                  color: theme.colors.text.primary,
                }}
              >
                Số lượng:
                <Text
                  color="xxs"
                  styles={{
                    marginLeft: 3,
                    color: getRelativeQuantityColor(record),
                  }}
                >
                  {reportQuantity(record)}
                </Text>
              </Row>
            }
            name={
              <Text lineNumber={1} title={name} weight="bold">
                {name}
              </Text>
            }
            size={SIZE.SMALL}
            width={240}
          />
        );
      },
    },
    {
      width: 180,
      colSpan: 400,
      title: (
        <Row alignItems="center" justifyContent="center">
          <Tag.CheckableTag
            checked={selectAllProducts}
            style={{
              width: 230,
              background: theme.colors.dropdown.main,
              color: theme.colors.primary.main,
              fontWeight: 600,
              fontSize: 13,
              borderRadius: 6,
            }}
            onChange={handleSelectAllProductsTags}
          >
            {getSelectAllTitle()}
          </Tag.CheckableTag>
        </Row>
      ),
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (_, record) => {
        const { wholesalePriceUnit } = record;

        return (
          <Column
            alignItems="flex-end"
            justifyContent="flex-end"
            styles={{ gap: 6 }}
          >
            {wholesalePriceUnit.map(item => (
              <CTag key={item.id}>
                {formatMoney(item.price)}đ/
                {genWholesaleUnit({
                  unitPrefix: productUnit.prefixObj[item.prefix],
                  unitWeight: item.weight,
                  unitPostfix: productUnit.postfixObj[item.postfix],
                })}
              </CTag>
            ))}
          </Column>
        );
      },
    },
  ];

  return (
    <Column
      justifyContent="flex-start"
      styles={{ height: '470px' }}
      width="472px"
    >
      <ShortFilterTable
        data={dataFilter}
        form={filterForm}
        handleFilterFormChange={handleFilterFormChange}
        name="sản phẩm"
        searchName="name"
        searchPlaceholder="Tìm kiếm sản phẩm"
        size={SIZE.SMALL}
      />
      <Space height={12} />
      <CTable
        {...(!isLoading &&
          (!selectAllProducts ||
            (selectAllProducts &&
              selectedProducts.length === pagination.total)) && {
            rowSelection: {
              type: 'checkbox',
              selectedRowKeys: selectedProducts,
              onChange: handleSelectProduct,
              preserveSelectedRowKeys: true,
            },
          })}
        columns={isLoading ? renderTableSkeleton(tableSkeleton) : columns}
        dataSource={isLoading ? getDummyArray(10) : products}
        locale={{
          emptyText: (
            <Empty
              description="Không tìm thấy sản phẩm"
              img={dataEmpty.CATEGORY}
              minHeight={280}
            />
          ),
        }}
        pagination={{
          ...pagination,
          pageSizeOptions: ['10', '20', '50', '100'],
        }}
        scroll={{
          y: 318,
        }}
        onChange={handleChangePagination}
      />
      <Space height={30} />
    </Column>
  );
};

export default ProductStepDesktop;
